import { c as _c } from "react/compiler-runtime";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { SlidersHorizontal } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useSearchParams } from "react-router";
import { api, type Product } from "@/api";
import { CatalogCategoryList } from "@/components/catalog/catalog-category-list";
import { CatalogFilters } from "@/components/catalog/catalog-filters";
import { UgcNotFoundNotice, UgcSuggestions, useUgcSearch } from "@/components/catalog/ugc-search-notice";
import { EmptyState } from "@/components/common/states";
import { ErrorState } from "@/components/common/states";
import { ProductCard } from "@/components/product/product-card";
import { PRODUCT_GRID_CLASS, ProductGrid, ProductGridSkeleton } from "@/components/product/product-grid";
import { Filter16Icon, SortIcon, XIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Chip } from "@/components/ui/chip";
import { Skeleton } from "@osmix/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { type AdItem, type AdPlacement, mixAds, useAdPlacements, withoutAdProducts } from "@/lib/ad-tracking";
import { DiscoverySourceProvider } from "@/lib/discovery-source";
import { expandProductTiles, type TileFilter } from "@/lib/product-tiles";
import { categoryPath, facetPath, resolveCategoryId, subcategoriesOf } from "@/lib/category-tree";
import { useArchivedCategories, useCategoryTree } from "@/lib/use-category-tree";
import { useFormatPrice } from "@/lib/use-format-price";
import { activeFilterCount, queryFromParams, SORTS } from "@/lib/product-query";
import { cn } from "@/lib/utils";
import { useDepartment, useSegment } from "@/store";
import { usePreferences } from "@/store/preferences";
import { usePageMeta } from "@/lib/use-page-meta";

// A quick-filter chip in the catalog bar — DS `Chip` sm (.chips-atom 32px, pad
// 0/12, gap 6; кадр 3888:79144): в покое #1F1F1F 3 % + 4 %, активный — #FA5C2E
// 5 % с обводкой, справа счётчик (кружок 16 #6982BA: «Размер (7) ×») и крестик.
function FilterChip(t0) {
  const $ = _c(6);
  const {
    label,
    active,
    badge,
    onOpen,
    onClear
  } = t0;
  const t1 = Boolean(active);
  const t2 = active ? badge : undefined;
  const t3 = active ? onClear : undefined;
  let t4;
  if ($[0] !== label || $[1] !== onOpen || $[2] !== t1 || $[3] !== t2 || $[4] !== t3) {
    t4 = <Chip selected={t1} count={t2} onClick={onOpen} onRemove={t3} removeLabel={"\u0421\u0431\u0440\u043E\u0441\u0438\u0442\u044C \u0444\u0438\u043B\u044C\u0442\u0440"}>{label}</Chip>;
    $[0] = label;
    $[1] = onOpen;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}

// The catalog / «Все товары» page — faceted product browsing. All filter, sort
// and search state lives in the URL; the gateway does the filtering. The top bar
// mirrors the Figma: breadcrumbs, a dark «Фильтры» pill + quick chips (Категория
// / Размер / Цена), and an inline sort control; the full facet panel opens in a
// sheet. The product grid runs full width, 4-up on desktop.
export function CatalogPage() {
  const $ = _c(250);
  const {
    t
  } = useTranslation();
  const fmt = useFormatPrice();
  const [params, setParams] = useSearchParams();
  usePageMeta();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const department = useDepartment();
  const city = usePreferences(_temp);
  const tree = useCategoryTree(department);
  const archivedTree = useArchivedCategories();
  const segment = useSegment();
  const departmentRootId = tree.isClothes ? undefined : tree.departmentRootIds[0];
  let t0;
  bb0: {
    let t1;
    if ($[0] !== params) {
      t1 = queryFromParams(params);
      $[0] = params;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== city || $[3] !== t1) {
      t2 = {
        ...t1,
        city
      };
      $[2] = city;
      $[3] = t1;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    const base = t2;
    if (departmentRootId && !params.get("categoryId")) {
      let t3;
      if ($[5] !== base || $[6] !== departmentRootId) {
        t3 = {
          ...base,
          categoryId: departmentRootId
        };
        $[5] = base;
        $[6] = departmentRootId;
        $[7] = t3;
      } else {
        t3 = $[7];
      }
      t0 = t3;
      break bb0;
    }
    t0 = base;
  }
  const query = t0;
  let t1;
  if ($[8] !== params) {
    t1 = params.get("search") ?? undefined;
    $[8] = params;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const search = t1;
  const filterCount = activeFilterCount(params);
  let t2;
  if ($[10] !== params) {
    t2 = params.toString();
    $[10] = params;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== city || $[13] !== department || $[14] !== t2) {
    t3 = ["products", "list", department, city, t2];
    $[12] = city;
    $[13] = department;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== query) {
    t4 = t5 => {
      const {
        pageParam
      } = t5;
      return api.products.list({
        ...query,
        page: pageParam
      });
    };
    $[16] = query;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== t3 || $[19] !== t4) {
    t5 = {
      queryKey: t3,
      queryFn: t4,
      initialPageParam: 1,
      getNextPageParam: _temp2
    };
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const page = useInfiniteQuery(t5);
  let t6;
  if ($[21] !== page.data?.pages) {
    const seen = new Set();
    t6 = (page.data?.pages ?? []).flatMap(_temp3).filter(p_0 => seen.has(p_0.id) ? false : (seen.add(p_0.id), true));
    $[21] = page.data?.pages;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const products = t6;
  const adPlace = search ? "search" : "catalog";
  let t7;
  if ($[23] !== adPlace || $[24] !== city || $[25] !== query.categoryId) {
    t7 = {
      place: adPlace,
      categoryId: query.categoryId,
      city,
      limit: 3
    };
    $[23] = adPlace;
    $[24] = city;
    $[25] = query.categoryId;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const adsQuery = useAdPlacements(t7);
  const moreRef = useRef(null);
  const {
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  } = page;
  let t8;
  let t9;
  if ($[27] !== fetchNextPage || $[28] !== hasNextPage || $[29] !== isFetchingNextPage) {
    t8 = () => {
      const node = moreRef.current;
      if (!node || !hasNextPage) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        if (entries.some(_temp4) && !isFetchingNextPage) {
          fetchNextPage();
        }
      }, {
        rootMargin: "800px 0px"
      });
      observer.observe(node);
      return () => observer.disconnect();
    };
    t9 = [hasNextPage, isFetchingNextPage, fetchNextPage];
    $[27] = fetchNextPage;
    $[28] = hasNextPage;
    $[29] = isFetchingNextPage;
    $[30] = t8;
    $[31] = t9;
  } else {
    t8 = $[30];
    t9 = $[31];
  }
  useEffect(t8, t9);
  const t10 = search ?? "";
  let t11;
  if ($[32] !== t10) {
    t11 = ["products", "facets", t10];
    $[32] = t10;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== search) {
    t12 = () => api.products.facets({
      search
    });
    $[34] = search;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== t11 || $[37] !== t12) {
    t13 = {
      queryKey: t11,
      queryFn: t12
    };
    $[36] = t11;
    $[37] = t12;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  const facets = useQuery(t13);
  const scopedCategoryId = query.categoryId;
  const t14 = search ?? "";
  const t15 = scopedCategoryId ?? "";
  let t16;
  if ($[39] !== t14 || $[40] !== t15) {
    t16 = ["products", "facets", "scoped", t14, t15];
    $[39] = t14;
    $[40] = t15;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== scopedCategoryId || $[43] !== search) {
    t17 = () => api.products.facets({
      search,
      categoryId: scopedCategoryId
    });
    $[42] = scopedCategoryId;
    $[43] = search;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  const t18 = Boolean(scopedCategoryId);
  let t19;
  if ($[45] !== t16 || $[46] !== t17 || $[47] !== t18) {
    t19 = {
      queryKey: t16,
      queryFn: t17,
      enabled: t18
    };
    $[45] = t16;
    $[46] = t17;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  const scopedFacets = useQuery(t19);
  let t20;
  if ($[49] !== facets.data || $[50] !== scopedCategoryId || $[51] !== scopedFacets) {
    t20 = scopedCategoryId && scopedFacets.data && facets.data ? {
      ...scopedFacets.data,
      categories: facets.data.categories
    } : facets.data;
    $[49] = facets.data;
    $[50] = scopedCategoryId;
    $[51] = scopedFacets;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  const panelFacets = t20;
  const sort = query.sort ?? "relevance";
  let t21;
  if ($[53] !== params || $[54] !== setParams) {
    t21 = value => {
      const next = new URLSearchParams(params);
      if (value === "relevance") {
        next.delete("sort");
      } else {
        next.set("sort", value);
      }
      next.delete("page");
      setParams(next);
    };
    $[53] = params;
    $[54] = setParams;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  const setSort = t21;
  let t22;
  if ($[56] !== params || $[57] !== setParams) {
    t22 = (...t23) => {
      const keys = t23;
      const next_0 = new URLSearchParams(params);
      for (const k of keys) {
        next_0.delete(k);
      }
      next_0.delete("page");
      setParams(next_0);
    };
    $[56] = params;
    $[57] = setParams;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  const clearKeys = t22;
  const total = page.data?.pages[0]?.meta.total ?? 0;
  const ugc = useUgcSearch(search, page.data?.pages[0]?.ugcRedirect);
  let categoryId;
  let categoryLabel;
  let openFilters;
  let priceActive;
  let priceLabel;
  let sizeCount;
  let t23;
  let t24;
  let t25;
  let t26;
  let t27;
  if ($[59] !== archivedTree || $[60] !== departmentRootId || $[61] !== facets || $[62] !== filterCount || $[63] !== fmt || $[64] !== params || $[65] !== search || $[66] !== segment || $[67] !== setParams || $[68] !== t || $[69] !== tree.categories || $[70] !== tree.isError || $[71] !== tree.isPending) {
    t27 = Symbol.for("react.early_return_sentinel");
    bb1: {
      categoryId = params.get("categoryId");
      const treePath = categoryId ? categoryPath(tree.categories, categoryId) : [];
      const categoryTrail = treePath.length ? treePath.map(_temp5) : categoryId ? facetPath(facets.data?.categories ?? [], categoryId).map(_temp6) : [];
      categoryLabel = categoryId ? categoryTrail[categoryTrail.length - 1]?.label ?? t("filters.category") : undefined;
      const subcategories = categoryId ? subcategoriesOf(tree.categories, categoryId, facets.data?.categories) : [];
      let t28;
      if ($[83] !== params) {
        t28 = params.getAll("size");
        $[83] = params;
        $[84] = t28;
      } else {
        t28 = $[84];
      }
      sizeCount = t28.length;
      let t29;
      if ($[85] !== params) {
        t29 = params.get("priceMin");
        $[85] = params;
        $[86] = t29;
      } else {
        t29 = $[86];
      }
      const priceMin = t29;
      let t30;
      if ($[87] !== params) {
        t30 = params.get("priceMax");
        $[87] = params;
        $[88] = t30;
      } else {
        t30 = $[88];
      }
      const priceMax = t30;
      priceActive = Boolean(priceMin || priceMax);
      let t31;
      if ($[89] !== fmt || $[90] !== priceActive || $[91] !== priceMax || $[92] !== priceMin || $[93] !== t) {
        t31 = priceActive ? priceMin && priceMax ? `${fmt(Number(priceMin))} – ${fmt(Number(priceMax))}` : priceMin ? `${t("filters.priceFrom")} ${fmt(Number(priceMin))}` : `${t("filters.priceTo")} ${fmt(Number(priceMax))}` : t("filters.price");
        $[89] = fmt;
        $[90] = priceActive;
        $[91] = priceMax;
        $[92] = priceMin;
        $[93] = t;
        $[94] = t31;
      } else {
        t31 = $[94];
      }
      priceLabel = t31;
      let t32;
      if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
        t32 = () => setFiltersOpen(true);
        $[95] = t32;
      } else {
        t32 = $[95];
      }
      openFilters = t32;
      const showList = !categoryId && !search && filterCount === 0 && params.get("view") !== "all";
      let t33;
      if ($[96] !== facets.data?.categories) {
        t33 = facets.data?.categories ?? [];
        $[96] = facets.data?.categories;
        $[97] = t33;
      } else {
        t33 = $[97];
      }
      const facetRoots = t33;
      let t34;
      if ($[98] !== departmentRootId || $[99] !== facetRoots) {
        t34 = departmentRootId ? facetRoots.find(c => c.value === departmentRootId)?.children ?? [] : facetRoots;
        $[98] = departmentRootId;
        $[99] = facetRoots;
        $[100] = t34;
      } else {
        t34 = $[100];
      }
      const listCategories = t34;
      let map;
      if ($[101] !== tree.categories) {
        map = new Map();
        const visit = nodes => {
          for (const node_1 of nodes) {
            if (node_1.imageUrl) {
              map.set(node_1.id, node_1.imageUrl);
            }
            visit(node_1.children ?? []);
          }
        };
        visit(tree.categories);
        $[101] = tree.categories;
        $[102] = map;
      } else {
        map = $[102];
      }
      const categoryImages = map;
      let t35;
      if ($[103] !== params || $[104] !== setParams) {
        t35 = id => {
          const next_1 = new URLSearchParams(params);
          next_1.set("categoryId", id);
          next_1.delete("view");
          next_1.delete("page");
          setParams(next_1);
        };
        $[103] = params;
        $[104] = setParams;
        $[105] = t35;
      } else {
        t35 = $[105];
      }
      const goToCategory = t35;
      let t36;
      if ($[106] !== params || $[107] !== setParams) {
        t36 = () => {
          const next_2 = new URLSearchParams(params);
          next_2.set("view", "all");
          next_2.delete("page");
          setParams(next_2);
        };
        $[106] = params;
        $[107] = setParams;
        $[108] = t36;
      } else {
        t36 = $[108];
      }
      const goToAll = t36;
      const categoryKnown = categoryId ? categoryPath(tree.categories, categoryId).length > 0 : true;
      if (categoryId && !tree.isPending && !tree.isError && tree.categories.length > 0 && (categoryKnown || archivedTree.settled)) {
        const resolved = resolveCategoryId(tree.categories, categoryId, segment, archivedTree.archived);
        if (resolved !== categoryId) {
          const next_3 = new URLSearchParams(params);
          if (resolved) {
            next_3.set("categoryId", resolved);
          } else {
            next_3.delete("categoryId");
          }
          next_3.delete("page");
          const qs = next_3.toString();
          const t37 = qs ? `/catalog?${qs}` : "/catalog";
          let t38;
          if ($[109] !== t37) {
            t38 = <Navigate to={t37} replace={true} />;
            $[109] = t37;
            $[110] = t38;
          } else {
            t38 = $[110];
          }
          t27 = t38;
          break bb1;
        }
      }
      if (showList) {
        let t37;
        if ($[111] !== t) {
          t37 = t("catalog.breadcrumbHome");
          $[111] = t;
          $[112] = t37;
        } else {
          t37 = $[112];
        }
        let t38;
        if ($[113] !== t37) {
          t38 = <Link to="/" className="hover:text-osmix-tangelo">{t37}</Link>;
          $[113] = t37;
          $[114] = t38;
        } else {
          t38 = $[114];
        }
        let t39;
        if ($[115] !== t) {
          t39 = t("catalog.title", {
            defaultValue: "\u041A\u0430\u0442\u0430\u043B\u043E\u0433"
          });
          $[115] = t;
          $[116] = t39;
        } else {
          t39 = $[116];
        }
        let t40;
        if ($[117] !== t39) {
          t40 = <span className="text-[#1F1F1F]">{t39}</span>;
          $[117] = t39;
          $[118] = t40;
        } else {
          t40 = $[118];
        }
        let t41;
        if ($[119] !== t38 || $[120] !== t40) {
          t41 = <nav aria-label={"\u0425\u043B\u0435\u0431\u043D\u044B\u0435 \u043A\u0440\u043E\u0448\u043A\u0438"} className="flex flex-wrap items-center gap-3 text-[14px] leading-[18px] text-black/50">{t38}{t40}</nav>;
          $[119] = t38;
          $[120] = t40;
          $[121] = t41;
        } else {
          t41 = $[121];
        }
        let t42;
        if ($[122] !== t) {
          t42 = t("catalog.title", {
            defaultValue: "\u041A\u0430\u0442\u0430\u043B\u043E\u0433"
          });
          $[122] = t;
          $[123] = t42;
        } else {
          t42 = $[123];
        }
        let t43;
        if ($[124] !== t42) {
          t43 = <h1 className="type-h1-bold">{t42}</h1>;
          $[124] = t42;
          $[125] = t43;
        } else {
          t43 = $[125];
        }
        let t44;
        if ($[126] !== categoryImages || $[127] !== departmentRootId || $[128] !== facets || $[129] !== goToAll || $[130] !== goToCategory || $[131] !== listCategories) {
          t44 = facets.isPending ? <div className="flex flex-col gap-3">{["a", "b", "c", "d", "e", "f"].map(_temp7)}</div> : facets.error ? <ErrorState onRetry={() => facets.refetch()} /> : <CatalogCategoryList categories={listCategories} onSelectCategory={goToCategory} onSelectAll={goToAll} departmentRootId={departmentRootId} imageById={categoryImages} />;
          $[126] = categoryImages;
          $[127] = departmentRootId;
          $[128] = facets;
          $[129] = goToAll;
          $[130] = goToCategory;
          $[131] = listCategories;
          $[132] = t44;
        } else {
          t44 = $[132];
        }
        let t45;
        if ($[133] !== t41 || $[134] !== t43 || $[135] !== t44) {
          t45 = <div className="flex flex-col gap-6">{t41}{t43}{t44}</div>;
          $[133] = t41;
          $[134] = t43;
          $[135] = t44;
          $[136] = t45;
        } else {
          t45 = $[136];
        }
        t27 = t45;
        break bb1;
      }
      t23 = "flex flex-col gap-6";
      let t37;
      if ($[137] !== t) {
        t37 = t("catalog.breadcrumbHome");
        $[137] = t;
        $[138] = t37;
      } else {
        t37 = $[138];
      }
      let t38;
      if ($[139] !== t37) {
        t38 = <Link to="/" className="hover:text-osmix-tangelo">{t37}</Link>;
        $[139] = t37;
        $[140] = t38;
      } else {
        t38 = $[140];
      }
      t24 = <nav aria-label={"\u0425\u043B\u0435\u0431\u043D\u044B\u0435 \u043A\u0440\u043E\u0448\u043A\u0438"} className="flex flex-wrap items-center gap-3 text-[14px] leading-[18px] text-black/50">{t38}{categoryTrail.map((node_2, index) => index < categoryTrail.length - 1 || search ? <Link key={node_2.value} to={`/catalog?categoryId=${encodeURIComponent(node_2.value)}`} className="hover:text-osmix-tangelo">{node_2.label}</Link> : <span key={node_2.value} className="text-[#1F1F1F]" data-testid="catalog-current-category">{node_2.label}</span>)}{(search || categoryTrail.length === 0) && <span className="text-[#1F1F1F]">{search ? t("catalog.searchResults", {
            query: search
          }) : t("catalog.allProducts")}</span>}</nav>;
      t25 = categoryLabel && !search && categoryTrail.length > 0 && <h1 className="type-h1-bold">{categoryLabel}</h1>;
      t26 = subcategories.length > 0 && <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:flex-wrap md:px-0" data-testid="catalog-subcategories">{subcategories.map(sub => <Chip key={sub.id} data-subcategory={sub.id} onClick={() => goToCategory(sub.id)}>{sub.name}{sub.count != null && <span className="text-[13px] text-ds-muted">{sub.count}</span>}</Chip>)}</div>;
    }
    $[59] = archivedTree;
    $[60] = departmentRootId;
    $[61] = facets;
    $[62] = filterCount;
    $[63] = fmt;
    $[64] = params;
    $[65] = search;
    $[66] = segment;
    $[67] = setParams;
    $[68] = t;
    $[69] = tree.categories;
    $[70] = tree.isError;
    $[71] = tree.isPending;
    $[72] = categoryId;
    $[73] = categoryLabel;
    $[74] = openFilters;
    $[75] = priceActive;
    $[76] = priceLabel;
    $[77] = sizeCount;
    $[78] = t23;
    $[79] = t24;
    $[80] = t25;
    $[81] = t26;
    $[82] = t27;
  } else {
    categoryId = $[72];
    categoryLabel = $[73];
    openFilters = $[74];
    priceActive = $[75];
    priceLabel = $[76];
    sizeCount = $[77];
    t23 = $[78];
    t24 = $[79];
    t25 = $[80];
    t26 = $[81];
    t27 = $[82];
  }
  if (t27 !== Symbol.for("react.early_return_sentinel")) {
    return t27;
  }
  let t28;
  if ($[141] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <Filter16Icon />;
    $[141] = t28;
  } else {
    t28 = $[141];
  }
  let t29;
  if ($[142] !== t) {
    t29 = t("filters.title");
    $[142] = t;
    $[143] = t29;
  } else {
    t29 = $[143];
  }
  let t30;
  if ($[144] !== openFilters || $[145] !== t29) {
    t30 = <Button size="md" onClick={openFilters} className="bg-ds-ink-2 hover:bg-ds-primary">{t28}{t29}</Button>;
    $[144] = openFilters;
    $[145] = t29;
    $[146] = t30;
  } else {
    t30 = $[146];
  }
  let t31;
  if ($[147] !== categoryLabel || $[148] !== t) {
    t31 = categoryLabel ?? t("filters.category");
    $[147] = categoryLabel;
    $[148] = t;
    $[149] = t31;
  } else {
    t31 = $[149];
  }
  const t32 = Boolean(categoryId);
  let t33;
  if ($[150] !== clearKeys) {
    t33 = () => clearKeys("categoryId");
    $[150] = clearKeys;
    $[151] = t33;
  } else {
    t33 = $[151];
  }
  let t34;
  if ($[152] !== openFilters || $[153] !== t31 || $[154] !== t32 || $[155] !== t33) {
    t34 = <FilterChip label={t31} active={t32} onOpen={openFilters} onClear={t33} />;
    $[152] = openFilters;
    $[153] = t31;
    $[154] = t32;
    $[155] = t33;
    $[156] = t34;
  } else {
    t34 = $[156];
  }
  let t35;
  if ($[157] !== t) {
    t35 = t("filters.size");
    $[157] = t;
    $[158] = t35;
  } else {
    t35 = $[158];
  }
  const t36 = sizeCount > 0;
  let t37;
  if ($[159] !== clearKeys) {
    t37 = () => clearKeys("size");
    $[159] = clearKeys;
    $[160] = t37;
  } else {
    t37 = $[160];
  }
  let t38;
  if ($[161] !== openFilters || $[162] !== sizeCount || $[163] !== t35 || $[164] !== t36 || $[165] !== t37) {
    t38 = <FilterChip label={t35} active={t36} badge={sizeCount} onOpen={openFilters} onClear={t37} />;
    $[161] = openFilters;
    $[162] = sizeCount;
    $[163] = t35;
    $[164] = t36;
    $[165] = t37;
    $[166] = t38;
  } else {
    t38 = $[166];
  }
  let t39;
  if ($[167] !== clearKeys) {
    t39 = () => clearKeys("priceMin", "priceMax");
    $[167] = clearKeys;
    $[168] = t39;
  } else {
    t39 = $[168];
  }
  let t40;
  if ($[169] !== openFilters || $[170] !== priceActive || $[171] !== priceLabel || $[172] !== t39) {
    t40 = <FilterChip label={priceLabel} active={priceActive} onOpen={openFilters} onClear={t39} />;
    $[169] = openFilters;
    $[170] = priceActive;
    $[171] = priceLabel;
    $[172] = t39;
    $[173] = t40;
  } else {
    t40 = $[173];
  }
  let t41;
  if ($[174] !== t) {
    t41 = t("catalog.sortBy");
    $[174] = t;
    $[175] = t41;
  } else {
    t41 = $[175];
  }
  let t42;
  if ($[176] !== t) {
    t42 = t("catalog.sortBy");
    $[176] = t;
    $[177] = t42;
  } else {
    t42 = $[177];
  }
  let t43;
  if ($[178] !== t42) {
    t43 = <SelectValue placeholder={t42} />;
    $[178] = t42;
    $[179] = t43;
  } else {
    t43 = $[179];
  }
  let t44;
  if ($[180] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <SortIcon className="size-6 text-ds-primary" />;
    $[180] = t44;
  } else {
    t44 = $[180];
  }
  let t45;
  if ($[181] !== t41 || $[182] !== t43) {
    t45 = <SelectTrigger aria-label={t41} className="ml-auto w-auto gap-2 rounded-[10px] bg-transparent px-0 py-0 text-[14px] font-medium leading-[18px] text-ds-primary shadow-none [&>svg:last-child]:hidden">{t43}{t44}</SelectTrigger>;
    $[181] = t41;
    $[182] = t43;
    $[183] = t45;
  } else {
    t45 = $[183];
  }
  let t46;
  if ($[184] !== t) {
    t46 = SORTS.map(s_0 => <SelectItem key={s_0} value={s_0}>{t(`catalog.sort.${s_0}`)}</SelectItem>);
    $[184] = t;
    $[185] = t46;
  } else {
    t46 = $[185];
  }
  let t47;
  if ($[186] !== t46) {
    t47 = <SelectContent align="end">{t46}</SelectContent>;
    $[186] = t46;
    $[187] = t47;
  } else {
    t47 = $[187];
  }
  let t48;
  if ($[188] !== setSort || $[189] !== sort || $[190] !== t45 || $[191] !== t47) {
    t48 = <Select value={sort} onValueChange={setSort}>{t45}{t47}</Select>;
    $[188] = setSort;
    $[189] = sort;
    $[190] = t45;
    $[191] = t47;
    $[192] = t48;
  } else {
    t48 = $[192];
  }
  let t49;
  if ($[193] !== t30 || $[194] !== t34 || $[195] !== t38 || $[196] !== t40 || $[197] !== t48) {
    t49 = <div className="flex flex-wrap items-center gap-4">{t30}{t34}{t38}{t40}{t48}</div>;
    $[193] = t30;
    $[194] = t34;
    $[195] = t38;
    $[196] = t40;
    $[197] = t48;
    $[198] = t49;
  } else {
    t49 = $[198];
  }
  let t50;
  if ($[199] !== t) {
    t50 = t("filters.allTitle", {
      defaultValue: "\u0412\u0441\u0435 \u0444\u0438\u043B\u044C\u0442\u0440\u044B"
    });
    $[199] = t;
    $[200] = t50;
  } else {
    t50 = $[200];
  }
  let t51;
  if ($[201] !== t50) {
    t51 = <SheetTitle className="text-[20px] font-semibold leading-[23px] text-osmix-text-1">{t50}</SheetTitle>;
    $[201] = t50;
    $[202] = t51;
  } else {
    t51 = $[202];
  }
  let t52;
  if ($[203] !== t) {
    t52 = t("common.close");
    $[203] = t;
    $[204] = t52;
  } else {
    t52 = $[204];
  }
  let t53;
  if ($[205] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <XIcon />;
    $[205] = t53;
  } else {
    t53 = $[205];
  }
  let t54;
  if ($[206] !== t52) {
    t54 = <SheetClose aria-label={t52} className="flex size-6 shrink-0 items-center justify-center text-ds-ink-2 outline-none focus-visible:ring-2 focus-visible:ring-ds-focus">{t53}</SheetClose>;
    $[206] = t52;
    $[207] = t54;
  } else {
    t54 = $[207];
  }
  let t55;
  if ($[208] !== t51 || $[209] !== t54) {
    t55 = <SheetHeader className="flex-row items-center justify-between gap-6 pt-[30px] pr-5 pb-2.5 pl-[30px]">{t51}{t54}</SheetHeader>;
    $[208] = t51;
    $[209] = t54;
    $[210] = t55;
  } else {
    t55 = $[210];
  }
  let t56;
  if ($[211] !== panelFacets || $[212] !== params || $[213] !== setParams || $[214] !== total) {
    t56 = panelFacets && <CatalogFilters facets={panelFacets} params={params} setParams={setParams} total={total} onApply={() => setFiltersOpen(false)} />;
    $[211] = panelFacets;
    $[212] = params;
    $[213] = setParams;
    $[214] = total;
    $[215] = t56;
  } else {
    t56 = $[215];
  }
  let t57;
  if ($[216] !== t56) {
    t57 = <div className="contents">{t56}</div>;
    $[216] = t56;
    $[217] = t57;
  } else {
    t57 = $[217];
  }
  let t58;
  if ($[218] !== t55 || $[219] !== t57) {
    t58 = <SheetContent side="left" showCloseButton={false} className="w-full gap-0 border-0 p-0 shadow-none sm:max-w-[610px]">{t55}{t57}</SheetContent>;
    $[218] = t55;
    $[219] = t57;
    $[220] = t58;
  } else {
    t58 = $[220];
  }
  let t59;
  if ($[221] !== filtersOpen || $[222] !== t58) {
    t59 = <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>{t58}</Sheet>;
    $[221] = filtersOpen;
    $[222] = t58;
    $[223] = t59;
  } else {
    t59 = $[223];
  }
  let t60;
  if ($[224] !== adPlace || $[225] !== adsQuery || $[226] !== filterCount || $[227] !== hasNextPage || $[228] !== isFetchingNextPage || $[229] !== page || $[230] !== products || $[231] !== query.colors || $[232] !== query.inStock || $[233] !== query.priceMax || $[234] !== query.priceMin || $[235] !== query.sale || $[236] !== search || $[237] !== setParams || $[238] !== t || $[239] !== total || $[240] !== ugc) {
    t60 = page.isPending || ugc === "redirecting" || ugc === "checking" ? <ProductGridSkeleton /> : page.error ? <ErrorState onRetry={() => page.refetch()} /> : total === 0 && ugc === "notFound" && search ? <><UgcNotFoundNotice code={search.trim()} /><UgcSuggestions /></> : total === 0 ? <EmptyState icon={SlidersHorizontal} title={t("catalog.empty")} hint={t("catalog.emptyHint")} action={filterCount > 0 ? <Button variant="gray" size="md" onClick={() => setParams(new URLSearchParams(search ? {
      search
    } : {
      view: "all"
    }))}>{t("catalogFilters.reset", {
        defaultValue: "\u0421\u0431\u0440\u043E\u0441\u0438\u0442\u044C \u0444\u0438\u043B\u044C\u0442\u0440\u044B"
      })}</Button> : undefined} /> : <DiscoverySourceProvider value={search ? "search" : "catalog"}><AdMixedGrid products={products} ads={adsQuery.data ?? []} place={adPlace} tileFilter={{
        colors: query.colors,
        priceMin: query.priceMin,
        priceMax: query.priceMax,
        sale: query.sale,
        inStockOnly: query.inStock
      }} />{hasNextPage && <div ref={moreRef} className="pt-5" aria-hidden="true">{isFetchingNextPage && <ProductGridSkeleton count={4} />}</div>}</DiscoverySourceProvider>;
    $[224] = adPlace;
    $[225] = adsQuery;
    $[226] = filterCount;
    $[227] = hasNextPage;
    $[228] = isFetchingNextPage;
    $[229] = page;
    $[230] = products;
    $[231] = query.colors;
    $[232] = query.inStock;
    $[233] = query.priceMax;
    $[234] = query.priceMin;
    $[235] = query.sale;
    $[236] = search;
    $[237] = setParams;
    $[238] = t;
    $[239] = total;
    $[240] = ugc;
    $[241] = t60;
  } else {
    t60 = $[241];
  }
  let t61;
  if ($[242] !== t23 || $[243] !== t24 || $[244] !== t25 || $[245] !== t26 || $[246] !== t49 || $[247] !== t59 || $[248] !== t60) {
    t61 = <div className={t23}>{t24}{t25}{t26}{t49}{t59}{t60}</div>;
    $[242] = t23;
    $[243] = t24;
    $[244] = t25;
    $[245] = t26;
    $[246] = t49;
    $[247] = t59;
    $[248] = t60;
    $[249] = t61;
  } else {
    t61 = $[249];
  }
  return t61;
}

// Сетка выдачи с рекламой (этап 8, П1). Без рекламы — обычная ProductGrid;
// с рекламой — те же плитки и та же сетка, рекламные карточки на местах
// 1, 5, 9 … с меткой «Реклама».
function _temp7(k_0) {
  return <div key={k_0} className="flex items-center gap-4 py-2.5"><Skeleton className="size-16 rounded-[4px]" /><Skeleton className="h-4 w-40" /></div>;
}
function _temp6(f) {
  return {
    value: f.value,
    label: f.label
  };
}
function _temp5(node_0) {
  return {
    value: node_0.id,
    label: node_0.name
  };
}
function _temp4(e) {
  return e.isIntersecting;
}
function _temp3(p) {
  return p.data;
}
function _temp2(last) {
  return last.meta.page < last.meta.totalPages ? last.meta.page + 1 : undefined;
}
function _temp(s) {
  return s.city;
}
function AdMixedGrid(t0) {
  const $ = _c(16);
  const {
    products,
    ads,
    place,
    tileFilter
  } = t0;
  if (ads.length === 0) {
    let t1;
    if ($[0] !== products || $[1] !== tileFilter) {
      t1 = <ProductGrid products={products} tileFilter={tileFilter} />;
      $[0] = products;
      $[1] = tileFilter;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let t1;
  let t2;
  let t3;
  if ($[3] !== ads || $[4] !== place || $[5] !== products || $[6] !== tileFilter) {
    const tiles = expandProductTiles(withoutAdProducts(products, ads), tileFilter);
    t1 = PRODUCT_GRID_CLASS;
    t2 = "catalog-grid-ads";
    let t4;
    if ($[10] !== place) {
      t4 = cell => cell.kind === "ad" ? <DiscoverySourceProvider key={`ad:${cell.ad.campaignId}:${cell.ad.productId}`} value="ads"><ProductCard product={cell.ad.product} ad={{
          campaignId: cell.ad.campaignId,
          placement: place
        }} /></DiscoverySourceProvider> : <ProductCard key={cell.item.key} product={cell.item.product} tile={cell.item} />;
      $[10] = place;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    t3 = mixAds(tiles, ads).map(t4);
    $[3] = ads;
    $[4] = place;
    $[5] = products;
    $[6] = tileFilter;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
  }
  let t4;
  if ($[12] !== t1 || $[13] !== t2 || $[14] !== t3) {
    t4 = <div className={t1} data-testid={t2}>{t3}</div>;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}